import { apiClient, getData, getPage } from '@/api/client'
import type { components } from '@/api/schema'
import {
  fundAnalyticsSummarySchema,
  fundDividendsSchema,
  fundHoldingsSchema,
  fundIncomeStatementSchema,
  fundPeriodSchema,
  fundSchema,
  fundSectorBreakdownSchema,
  fundSectorCountSchema,
  fundStockCountSchema,
  fundTrendsSchema,
  industryHoldingsSchema,
  monthlyHoldingsSchema,
  navHistorySchema,
  popularStockSchema,
  sectorCompanySchema,
  stockHoldersSchema,
  type Fund,
  type FundAnalyticsSummary,
  type FundDividends,
  type FundHoldings,
  type FundIncomeStatement,
  type FundListQuery,
  type FundPeriod,
  type FundSectorBreakdown,
  type FundSectorCount,
  type FundStatus,
  type FundStockCount,
  type FundTrends,
  type FundTrendsQuery,
  type FundType,
  type IndustryHoldings,
  type MonthlyHoldings,
  type NavHistory,
  type PopularStock,
  type SectorCompany,
  type StockHolders,
} from '@/schemas/mutual-funds'

/**
 * The mutual-funds seam: twelve public endpoints in, app types out.
 *
 * Structured like `features/brokers/api/brokerService.ts`: wire types come from
 * `@/api/schema`, never restated, so a regenerated `schema.d.ts` that renames
 * a field breaks the build HERE; every mapper's result then goes through its
 * Zod schema before it leaves the module.
 *
 * `period`, wherever a read takes one, is a holdings-period LABEL
 * ("Bhadra 2083", from `/periods/`) or `null` for "the backend's latest". Null
 * sends no parameter at all rather than an empty one, so the backend picks
 * its own default instead of being asked for a month called "".
 */

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

const periodParam = (period: string | null) => (period === null ? {} : { period })

/**
 * A name the backend may send as `""` as well as null. Both mean "none", and
 * the schemas' `min(1)` would otherwise reject a whole list for one blank.
 */
const blankToNull = (value: string | null): string | null =>
  value === null || value.trim() === '' ? null : value

/** A fund symbol, a stock symbol or a sector code: letters and digits only. */
const isTickerSymbol = (symbol: string): boolean => /^[A-Za-z0-9]+$/.test(symbol)

/**
 * `fund_type`/`status` are CharFields on the wire, not choices (`fundTypeSchema`'s
 * own comment) — a value neither branch names (one `INTERVAL` row has been
 * seen) is an ingestion fact, not a shape error. Mapped to `null` here rather
 * than parsed against the closed union, so an unrecognised value can't blank
 * a page that never branches on either field except display.
 */
const knownFundType = (value: string): FundType | null =>
  value === 'CLOSE_ENDED' || value === 'OPEN_ENDED' ? value : null

const knownFundStatus = (value: string): FundStatus | null =>
  value === 'ACTIVE' || value === 'MATURED' ? value : null

/** mm-be's stand-in for "this scheme's maturity was never confirmed". */
const PLACEHOLDER_MATURITY_DATE = '2050-01-01'
/** mm-be's stand-in for "this scheme's authorised size was never confirmed". */
const PLACEHOLDER_FUND_SIZE = '1000000000.00'

/**
 * TODO(tech-debt): ask the backend to answer `fund_size`/`maturity_date` as
 * null for a scheme it never confirmed, instead of a stand-in row that reads
 * as real data (26 of 57 close-ended funds today: `maturity_date`
 * "2050-01-01", `fund_size` exactly "1000000000.00", no `start_date`, no
 * `maturity_period` — the four always travel together).
 */
const isPlaceholderFund = (wire: Wire<'FundRow'>): boolean =>
  wire.maturity_date === PLACEHOLDER_MATURITY_DATE &&
  wire.start_date === null &&
  wire.maturity_period === null

// ── Mappers ──────────────────────────────────────────────────────────────────
//
// Exported so each one is testable on a fixture without a request. They map
// only; the schema at the call site checks what they produce.

/** `aum` is dropped on purpose — see `fundSchema` for why its unit can't be trusted. */
export const mapFund = (wire: Wire<'FundRow'>) => ({
  symbol: wire.symbol,
  name: blankToNull(wire.name),
  fundType: knownFundType(wire.fund_type),
  status: knownFundStatus(wire.status),
  fundManager: blankToNull(wire.fund_manager),
  fundManagerLogo: blankToNull(wire.fund_manager_logo),
  fundSize:
    wire.fund_size === PLACEHOLDER_FUND_SIZE && isPlaceholderFund(wire) ? null : wire.fund_size,
  nav: wire.nav,
  navPeriod: blankToNull(wire.nav_period),
  maturityDate: wire.maturity_date === PLACEHOLDER_MATURITY_DATE ? null : wire.maturity_date,
  maturityYears: wire.maturity_period,
  startDate: wire.start_date,
  nepseIndexAtStart: wire.nepse_index_at_start,
  isListed: wire.is_listed,
  marketPrice: wire.market_price,
  premiumDiscountPercent: wire.premium_discount_percent,
})

/**
 * THE switch for Analytics' third figure, "Bought by funds this month" (owner F1, 2026-10-29).
 * `/analytics/summary/` serves no net-purchase value, so this is `false`: the cell keeps its label
 * over "Not available yet". Flip it once mm-be adds `net_bought_value`
 * (`mm-mf-2026-10-29/asks/analytics-summary.md`) and `pnpm gen:api` knows the field.
 */
export const FUND_NET_BOUGHT_AVAILABLE: boolean = false

/** The proposed summary field, not in the generated schema yet (see `FUND_NET_BOUGHT_AVAILABLE`). */
interface ProposedSummaryFields {
  net_bought_value?: string | null
}

export const mapSummary = (wire: Wire<'AnalyticsSummary'> & ProposedSummaryFields) => ({
  period: blankToNull(wire.period),
  fundsTracked: wire.funds_tracked,
  uniqueStocks: wire.unique_stocks,
  totalShares: wire.total_shares,
  netBoughtValue: wire.net_bought_value ?? null,
})

export const mapStockCount = (wire: Wire<'FundStockRow'>) => ({
  symbol: wire.symbol,
  name: blankToNull(wire.name),
  stockCount: wire.stock_count,
  value: wire.value,
})

export const mapSectorCount = (wire: Wire<'FundSectorRow'>) => ({
  symbol: wire.symbol,
  name: blankToNull(wire.name),
  sectorCount: wire.sector_count,
  value: wire.value,
})

export const mapPopularStock = (wire: Wire<'PopularStockRow'>) => ({
  symbol: wire.symbol,
  sector: blankToNull(wire.sector),
  fundCount: wire.fund_count,
  value: wire.value,
})

export const mapSectorSlice = (wire: Wire<'SectorSlice'>) => ({
  sector: blankToNull(wire.sector),
  name: blankToNull(wire.name),
  stockCount: wire.stock_count,
  value: wire.value,
  pct: wire.pct,
})

export const mapSectorBreakdown = (wire: Wire<'FundSectorBreakdown'>) => ({
  fund: wire.fund,
  value: wire.value,
  sectors: wire.sectors.map(mapSectorSlice),
})

/**
 * THE WIRE'S 0 IS "DID NOT FILE", NOT "HELD NOTHING". A fund that reported
 * holdings holds at least one stock, and the zeros sit exactly where a fund
 * skipped a monthly report (NIBLSF `[57, 55, …, 0, 0, 0, 0, 0, 127]`), so a
 * chart that drew them would plunge to the floor and back for every missed
 * filing. Null here makes the chart draw a gap instead.
 */
export const mapTrends = (wire: Wire<'TrendChart'>) => ({
  periods: wire.periods,
  series: wire.series.map((series) => ({
    symbol: series.symbol,
    data: series.data.map((count) => (count === 0 ? null : count)),
  })),
})

/**
 * `SectorAllocation` is the industry-holdings payload, whatever the name says:
 * mm-be's mutual-funds serializer shares its component name with the
 * portfolio allocation's sector row, and drf-spectacular kept only this one —
 * which is also why `AllocationReport.sectors` is mistyped in `schema.d.ts`.
 */
export const mapIndustryHoldings = (wire: Wire<'SectorAllocation'>) => ({
  period: blankToNull(wire.period),
  total: wire.total,
  sectors: wire.sectors.map(mapSectorSlice),
})

export const mapSectorCompany = (wire: Wire<'SectorCompanyRow'>) => ({
  symbol: wire.symbol,
  name: blankToNull(wire.name),
  funds: wire.funds,
  shares: wire.shares,
  value: wire.value,
})

/**
 * A wire shape whose `K` fields the spec names but the live API may not send yet (mm-be's
 * feat/web-asks-funds-portfolio, in the spec ahead of its deploy): read as optional, so a screen
 * works the same before and after it.
 */
type Arriving<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>

export const mapMonthlyHoldings = (
  wire: Omit<Wire<'MonthlyHoldings'>, 'companies'> & {
    companies: readonly Arriving<Wire<'CompanyHoldingRow'>, 'funds'>[]
  },
) => ({
  period: blankToNull(wire.period),
  total: wire.total,
  companies: wire.companies.map((row) => ({
    symbol: row.symbol,
    name: blankToNull(row.name),
    funds: row.funds ?? null,
    shares: row.shares,
    value: row.value,
    pct: row.pct,
  })),
})

export const mapDividends = (wire: Wire<'DividendsOverview'>) => ({
  distributable: wire.distributable.map((row) => ({
    symbol: row.symbol,
    name: blankToNull(row.name),
    year: row.year,
    month: blankToNull(row.month),
    dividend: row.dividend,
  })),
  openEnded: wire.open_ended.map((row) => ({
    symbol: row.symbol,
    name: blankToNull(row.name),
    totalPercent: row.total_percent,
    perYearPercent: row.per_year_percent,
  })),
})

export const mapFundHoldings = (wire: Wire<'FundHoldings'>) => ({
  fund: wire.fund,
  period: blankToNull(wire.period),
  total: wire.total,
  stockCount: wire.stock_count,
  companies: wire.companies.map((row) => ({
    symbol: row.symbol,
    name: blankToNull(row.name),
    sector: blankToNull(row.sector),
    sectorName: blankToNull(row.sector_name),
    shares: row.shares,
    value: row.value,
    pct: row.pct,
  })),
})

export const mapStockHolders = (wire: Wire<'StockHolders'>) => ({
  symbol: wire.symbol,
  name: blankToNull(wire.name),
  period: blankToNull(wire.period),
  shares: wire.shares,
  value: wire.value,
  funds: wire.funds.map((row) => ({
    symbol: row.symbol,
    name: blankToNull(row.name),
    shares: row.shares,
    value: row.value,
    pct: row.pct,
  })),
})

export const mapNavHistory = (wire: Wire<'NavHistory'>) => ({
  fund: wire.fund,
  points: wire.points.map((point) => ({
    period: point.period,
    nav: point.nav,
    netAssets: point.net_assets,
    units: point.units,
  })),
})

export const mapIncomeStatement = (wire: Wire<'FundIncomeStatement'>) => ({
  fund: wire.fund,
  years: wire.years.map((year) => ({
    fiscalYear: year.fiscal_year,
    period: year.period,
    income: year.income,
    totalIncome: year.total_income,
    expenses: year.expenses,
    totalExpenses: year.total_expenses,
    netProfit: year.net_profit,
    netProfitPerUnit: year.net_profit_per_unit,
    units: year.units,
  })),
})

// ── The endpoints ────────────────────────────────────────────────────────────

export const fundService = {
  /** The fund directory, optionally one Price vs NAV category. Unpaged (71 funds). */
  list: async (query: FundListQuery): Promise<Fund[]> => {
    const { rows } = await getPage<Wire<'FundRow'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/', {
        params: {
          query: {
            ...(query.category === undefined ? {} : { category: query.category }),
          },
        },
      }),
    )
    // TODO(tech-debt): mm-be's directory has listed at least one filesystem
    // artifact (`.DS_Store`) as an active fund. A real symbol is always
    // letters and digits, so this drops anything else at the boundary rather
    // than parsing it into the app as a fund.
    const tickerRows = rows.filter((row) => isTickerSymbol(row.symbol))
    return fundSchema.array().parse(tickerRows.map(mapFund))
  },

  /** Every holdings period with filings, newest first. */
  periods: async (): Promise<FundPeriod[]> => {
    const { rows } = await getPage<Wire<'PeriodOption'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/periods/'),
    )
    return fundPeriodSchema.array().parse(rows)
  },

  /** Funds tracked, unique stocks, total shares — Analytics' three tiles. */
  summary: async (period: string | null): Promise<FundAnalyticsSummary> => {
    const data = await getData<Wire<'AnalyticsSummary'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/analytics/summary/', {
        params: { query: periodParam(period) },
      }),
    )
    return fundAnalyticsSummarySchema.parse(mapSummary(data))
  },

  /** Funds by how many distinct stocks they hold, most first. Also Fund rankings. */
  stockCount: async (period: string | null): Promise<FundStockCount[]> => {
    const { rows } = await getPage<Wire<'FundStockRow'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/analytics/stock-count/', {
        params: { query: periodParam(period) },
      }),
    )
    return fundStockCountSchema.array().parse(rows.map(mapStockCount))
  },

  /** Stocks by how many funds hold them, most first (the top 20). */
  popular: async (period: string | null): Promise<PopularStock[]> => {
    const { rows } = await getPage<Wire<'PopularStockRow'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/analytics/popular/', {
        params: { query: periodParam(period) },
      }),
    )
    return popularStockSchema.array().parse(rows.map(mapPopularStock))
  },

  /** Funds by how many sectors they span, most first. */
  sectorCounts: async (period: string | null): Promise<FundSectorCount[]> => {
    const { rows } = await getPage<Wire<'FundSectorRow'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/analytics/sectors/', {
        params: { query: periodParam(period) },
      }),
    )
    return fundSectorCountSchema.array().parse(rows.map(mapSectorCount))
  },

  /** One fund's value split across its sectors. An unknown fund answers empty, not 404. */
  sectorBreakdown: async (fund: string, period: string | null): Promise<FundSectorBreakdown> => {
    const data = await getData<Wire<'FundSectorBreakdown'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/analytics/sectors/{fund}/', {
        params: { path: { fund }, query: periodParam(period) },
      }),
    )
    return fundSectorBreakdownSchema.parse(mapSectorBreakdown(data))
  },

  /**
   * Stocks held per month for the named funds.
   *
   * The array becomes the wire's comma-separated `funds` here and nowhere
   * else. Never called without funds: the backend would answer all of them.
   */
  trends: async (query: FundTrendsQuery): Promise<FundTrends> => {
    const data = await getData<Wire<'TrendChart'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/analytics/trends/', {
        params: {
          query: { funds: query.funds.join(',') },
        },
      }),
    )
    return fundTrendsSchema.parse(mapTrends(data))
  },

  /** All funds' holdings by sector — the industry donut. */
  industryHoldings: async (period: string | null): Promise<IndustryHoldings> => {
    const data = await getData<Wire<'SectorAllocation'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/industry-holdings/', {
        params: { query: periodParam(period) },
      }),
    )
    return industryHoldingsSchema.parse(mapIndustryHoldings(data))
  },

  /**
   * The companies inside one sector, across every fund. `sector` is the CODE
   * (`BANKING`) — the display name answers an empty list, not an error.
   */
  sectorCompanies: async (sector: string, period: string | null): Promise<SectorCompany[]> => {
    const { rows } = await getPage<Wire<'SectorCompanyRow'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/industry-holdings/companies/', {
        params: { query: { sector, ...periodParam(period) } },
      }),
    )
    return sectorCompanySchema.array().parse(rows.map(mapSectorCompany))
  },

  /** Every company the funds hold, by total value, with its share of the whole. */
  monthlyHoldings: async (period: string | null): Promise<MonthlyHoldings> => {
    const data = await getData<Wire<'MonthlyHoldings'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/monthly-holdings/', {
        params: { query: periodParam(period) },
      }),
    )
    return monthlyHoldingsSchema.parse(mapMonthlyHoldings(data))
  },

  /**
   * Every stock one fund holds, biggest first. No `period` is the fund's own latest filing (funds
   * file on different months), as `/analytics/sectors/{fund}/` reads it. 404 for an unknown fund.
   */
  fundHoldings: async (fund: string, period: string | null): Promise<FundHoldings> => {
    const data = await getData<Wire<'FundHoldings'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/{fund}/holdings/', {
        params: { path: { fund }, query: periodParam(period) },
      }),
    )
    return fundHoldingsSchema.parse(mapFundHoldings(data))
  },

  /**
   * Every fund holding one stock, biggest first. No `period` is the Industry tab's month, so the
   * count matches its Funds column. A real company no fund holds is an empty list; a symbol no
   * company has is 404 `COMPANY_NOT_FOUND`.
   */
  stockHolders: async (symbol: string, period: string | null): Promise<StockHolders> => {
    const data = await getData<Wire<'StockHolders'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/holders/{symbol}/', {
        params: { path: { symbol }, query: periodParam(period) },
      }),
    )
    return stockHoldersSchema.parse(mapStockHolders(data))
  },

  /** A fund's last `months` monthly NAV filings (1–36), newest first. 404 for an unknown fund. */
  navHistory: async (fund: string, months: number): Promise<NavHistory> => {
    const data = await getData<Wire<'NavHistory'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/{fund}/nav/', {
        params: { path: { fund }, query: { months } },
      }),
    )
    return navHistorySchema.parse(mapNavHistory(data))
  },

  /** A fund's year-end income statements, newest first (up to five). 404 for an unknown fund. */
  incomeStatement: async (fund: string): Promise<FundIncomeStatement> => {
    const data = await getData<Wire<'FundIncomeStatement'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/{fund}/income-statement/', {
        params: { path: { fund } },
      }),
    )
    return fundIncomeStatementSchema.parse(mapIncomeStatement(data))
  },

  /** Distributable dividends (close-ended) and the open-ended dividend record. */
  dividends: async (): Promise<FundDividends> => {
    const data = await getData<Wire<'DividendsOverview'>>(() =>
      apiClient.GET('/api/v5/mutual-funds/dividends/'),
    )
    return fundDividendsSchema.parse(mapDividends(data))
  },
}
